import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { paymentsAPI } from '../services/api'

const PLAN_DETAILS = {
  starter_monthly: {
    name: 'Starter Plan - Monthly',
    price: '\u20B92,999',
    duration: '1 month',
    perMonth: '\u20B92,999/mo',
    onboardingFee: '\u20B92,999',
    onboardingAmount: 2999,
    totalWithOnboarding: '\u20B95,998',
    features: [
      'Full chat capabilities',
      'Email integration',
      'CRM integration',
      'Guided onboarding',
    ],
    tagline: 'Best for evaluating with real business use cases',
  },
  growth_6month: {
    name: 'Growth Plan - 6 Months',
    price: '\u20B914,994',
    duration: '6 months',
    perMonth: '\u20B92,499/mo',
    onboardingFee: '\u20B9999',
    onboardingAmount: 999,
    totalWithOnboarding: '\u20B915,993',
    features: [
      'Everything in Starter',
      'Priority onboarding',
      'Faster support',
    ],
    tagline: 'Balanced commitment with strong savings and real ROI potential',
    popular: true,
  },
  pro_annual: {
    name: 'Pro Plan - Annual',
    price: '\u20B923,988',
    duration: '12 months',
    perMonth: '\u20B91,999/mo',
    onboardingFee: 'FREE',
    onboardingAmount: 0,
    totalWithOnboarding: '\u20B923,988',
    features: [
      'Everything in Growth',
      'Dedicated onboarding support',
      'Priority assistance',
    ],
    tagline: 'Maximum savings. Built for businesses serious about growth',
    bestValue: true,
  },
}

function loadCashfreeScript() {
  return new Promise((resolve) => {
    if (window.Cashfree) {
      resolve(true)
      return
    }
    const script = document.createElement('script')
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

export default function Subscribe() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const planParam = searchParams.get('plan') || 'growth_6month'

  const [planType, setPlanType] = useState(
    PLAN_DETAILS[planParam] ? planParam : 'growth_6month'
  )
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [cashfreeAvailable, setCashfreeAvailable] = useState(false)
  const [subscription, setSubscription] = useState(null)

  const token = localStorage.getItem('auth_token')

  useEffect(() => {
    if (!token) {
      sessionStorage.setItem('pending_plan', planType)
      navigate(`/login?redirect=/subscribe&plan=${planType}`)
      return
    }

    // Check if Cashfree is configured and get current subscription
    const init = async () => {
      try {
        const [plansRes, subRes] = await Promise.all([
          paymentsAPI.getPlans(),
          paymentsAPI.getSubscription(),
        ])
        setCashfreeAvailable(plansRes.data.cashfree_available)
        setSubscription(subRes.data)
      } catch (err) {
        console.error('Init error:', err)
      } finally {
        setChecking(false)
      }
    }
    init()
  }, [token, navigate, planType])

  const isAlreadyPaid =
    ['starter', 'growth', 'pro'].includes(subscription?.plan) &&
    subscription?.status === 'active' &&
    subscription?.end_date &&
    new Date(subscription.end_date) > new Date()

  const handlePay = async () => {
    setLoading(true)
    setError('')

    try {
      // Load Cashfree script
      const loaded = await loadCashfreeScript()
      if (!loaded) {
        setError('Failed to load payment gateway. Please try again.')
        setLoading(false)
        return
      }

      // Create order
      const { data } = await paymentsAPI.createOrder(planType)

      // Initialize Cashfree and open checkout
      const cashfree = window.Cashfree({
        mode: data.mode === 'sandbox' ? 'sandbox' : 'production',
      })

      const checkoutResult = await cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: '_modal',
      })

      if (checkoutResult.error) {
        setError(checkoutResult.error.message || 'Payment failed. Please try again.')
        setLoading(false)
        return
      }

      if (checkoutResult.redirect) {
        // Payment requires redirect — handled by Cashfree
        return
      }

      // Payment completed — verify on backend
      if (checkoutResult.paymentDetails) {
        try {
          await paymentsAPI.verifyPayment({
            order_id: data.order_id,
          })
          setSuccess(true)
          sessionStorage.removeItem('pending_plan')
          setTimeout(() => navigate('/customer-dashboard'), 2500)
        } catch (verifyErr) {
          setError('Payment was received but verification failed. Please contact support.')
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Something went wrong'
      setError(detail)
      setLoading(false)
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-10 rounded-xl shadow-md text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-4">Your plan is now active.</p>
          <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
          <div className="flex justify-center mt-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    )
  }

  const plan = PLAN_DETAILS[planType]
  const showOnboardingFee = subscription && !subscription.setup_fee_paid && plan.onboardingAmount > 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-purple-600">CognaAssist</Link>
          <Link to="/customer-dashboard" className="text-sm text-gray-600 hover:text-purple-600">
            Back to Dashboard
          </Link>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">Upgrade Your Plan</h1>
        <p className="text-gray-600 text-center mb-8">Choose the plan that fits your business</p>

        {isAlreadyPaid && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-center">
            <p className="text-green-800 font-medium">You already have an active paid plan!</p>
            <p className="text-green-600 text-sm mt-1">
              Active until {new Date(subscription.end_date).toLocaleDateString()}
            </p>
            <Link to="/customer-dashboard" className="text-purple-600 font-medium text-sm mt-2 inline-block hover:underline">
              Go to Dashboard
            </Link>
          </div>
        )}

        {/* Plan selector */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Select Plan</h3>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setPlanType('starter_monthly')}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                planType === 'starter_monthly'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold text-gray-900 text-sm">Starter</div>
              <div className="text-lg font-bold text-purple-600">{'\u20B9'}2,999</div>
              <div className="text-xs text-gray-500">Monthly</div>
            </button>
            <button
              onClick={() => setPlanType('growth_6month')}
              className={`p-4 rounded-lg border-2 text-left transition-colors relative ${
                planType === 'growth_6month'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="absolute -top-2 right-2">
                <span className="bg-purple-600 text-white text-[10px] px-2 py-0.5 rounded-full">Popular</span>
              </div>
              <div className="font-semibold text-gray-900 text-sm">Growth</div>
              <div className="text-lg font-bold text-purple-600">{'\u20B9'}2,499</div>
              <div className="text-xs text-gray-500">6 months</div>
            </button>
            <button
              onClick={() => setPlanType('pro_annual')}
              className={`p-4 rounded-lg border-2 text-left transition-colors relative ${
                planType === 'pro_annual'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="absolute -top-2 right-2">
                <span className="bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full">Best Value</span>
              </div>
              <div className="font-semibold text-gray-900 text-sm">Pro</div>
              <div className="text-lg font-bold text-purple-600">{'\u20B9'}1,999</div>
              <div className="text-xs text-gray-500">Annual</div>
            </button>
          </div>
        </div>

        {/* Price breakdown */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Order Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-700">{plan.name}</span>
              <span className="font-medium">{plan.price}</span>
            </div>
            {showOnboardingFee && (
              <div className="flex justify-between">
                <span className="text-gray-700">Onboarding &amp; AI Training</span>
                <span className="font-medium">{plan.onboardingFee}</span>
              </div>
            )}
            {planType === 'pro_annual' && !subscription?.setup_fee_paid && (
              <div className="flex justify-between">
                <span className="text-gray-700">Onboarding</span>
                <span className="font-medium text-green-600">FREE</span>
              </div>
            )}
            <div className="border-t pt-3 flex justify-between">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="font-bold text-lg text-purple-600">
                {showOnboardingFee ? plan.totalWithOnboarding : plan.price}
              </span>
            </div>
          </div>
          {plan.tagline && (
            <p className="text-xs text-gray-500 mt-3">{plan.tagline}</p>
          )}
        </div>

        {/* Features */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3">What you get</h3>
          <ul className="space-y-2">
            {plan.features.map((feature) => (
              <li key={feature} className="flex items-center text-sm text-gray-700">
                <span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Pay button or fallback */}
        {cashfreeAvailable ? (
          <button
            onClick={handlePay}
            disabled={loading || isAlreadyPaid}
            className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></span>
                Processing...
              </span>
            ) : (
              'Proceed to Pay'
            )}
          </button>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-gray-600 mb-2">Payment integration coming soon!</p>
            <p className="text-sm text-gray-500">
              Contact us at{' '}
              <a href="mailto:hello@cognavia.ai" className="text-purple-600 font-medium hover:underline">
                hello@cognavia.ai
              </a>{' '}
              to subscribe.
            </p>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center mt-4">
          Payments are processed securely via Cashfree. By subscribing, you agree to our terms of service.
        </p>
      </div>
    </div>
  )
}
